import { getApiBaseUrl } from "../lib/env";
import {
  clearSession,
  readSession as readTokensOnly,
  writeSession,
} from "../lib/session";
import type { AuthTokens } from "../types/auth";

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export type ApiErrorDetail = {
  field?: string;
  message: string;
};

export type ApiMeta = {
  requestId?: string;
  pagination?: {
    cursor?: string | null;
    nextCursor?: string | null;
    limit?: number;
    total?: number;
  };
};

export type ApiEnvelope<T> = {
  success: boolean;
  message?: string;
  data?: T | null;
  error?: {
    code?: string;
    details?: ApiErrorDetail[];
  } | null;
  meta?: ApiMeta;
};

export type ApiRequestOptions<TBody = unknown, TResponse = unknown> = {
  /**
  * Path relative to API base (e.g., "/system").
   */
  path: string;
  method?: HttpMethod;
  body?: TBody;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /**
   * If true, short-circuits to mock mode (no network).
   */
  useMock?: boolean;
  /**
   * Optional mock payload to return when in mock mode or when API base is absent.
   */
  mockResponse?: TResponse;
  /**
   * Optional artificial delay for mock responses.
   */
  delayMs?: number;
  /**
   * Skip Authorization header (e.g., login, refresh).
   */
  auth?: boolean;
  /**
   * Allow automatic token refresh on 401. Defaults to true.
   */
  allowRefresh?: boolean;
};

export class ApiError extends Error {
  httpStatus: number;
  errorCode?: string;
  errorDetails?: ApiErrorDetail[];
  rawBody?: unknown;

  constructor(init: {
    message: string;
    httpStatus: number;
    errorCode?: string;
    errorDetails?: ApiErrorDetail[];
    rawBody?: unknown;
  }) {
    super(init.message);
    this.name = "ApiError";
    this.httpStatus = init.httpStatus;
    this.errorCode = init.errorCode;
    this.errorDetails = init.errorDetails;
    this.rawBody = init.rawBody;
  }
}

let refreshInFlight: Promise<AuthTokens | null> | null = null;

function parseStoredTokens(): AuthTokens | null {
  const rawTokens = readTokensOnly();
  if (rawTokens) return rawTokens;

  try {
    const raw = window.localStorage.getItem("kkp_admin_session_v1");
    if (!raw) return null;
    const parsed = JSON.parse(raw) as any;
    const tokens: AuthTokens | undefined = parsed?.tokens ?? parsed;
    if (tokens?.access && tokens?.refresh) {
      return { access: String(tokens.access), refresh: String(tokens.refresh) };
    }
    return null;
  } catch {
    return null;
  }
}

function persistTokens(tokens: AuthTokens) {
  try {
    const raw = window.localStorage.getItem("kkp_admin_session_v1");
    if (!raw) {
      writeSession(tokens);
      return;
    }
    const parsed = JSON.parse(raw) as any;
    if (parsed && typeof parsed === "object") {
      if (parsed.tokens) {
        parsed.tokens = { access: tokens.access, refresh: tokens.refresh };
        window.localStorage.setItem(
          "kkp_admin_session_v1",
          JSON.stringify(parsed),
        );
        return;
      }
      if (parsed.access && parsed.refresh) {
        window.localStorage.setItem(
          "kkp_admin_session_v1",
          JSON.stringify({ access: tokens.access, refresh: tokens.refresh }),
        );
        return;
      }
    }
    writeSession(tokens);
  } catch {
    writeSession(tokens);
  }
}

function hardLogout() {
  try {
    clearSession();
  } catch {
    // ignore
  }
  try {
    window.localStorage.setItem("kkp_admin_session_expired", "1");
  } catch {
    // ignore
  }
  window.location.hash = "#/login";
}

function buildHeaders(
  baseHeaders: Record<string, string> | undefined,
  body: unknown,
  token: string | null,
  auth: boolean,
): Record<string, string> {
  const headers: Record<string, string> = { ...(baseHeaders ?? {}) };
  if (auth && token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const hasBody = body !== undefined && body !== null;
  const isJsonBody =
    hasBody && typeof body === "object" && !(body instanceof FormData);
  if (hasBody && isJsonBody && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }
  return headers;
}

async function doFetch<T>(
  url: string,
  init: RequestInit,
  expectsAuth: boolean,
): Promise<T> {
  const res = await fetch(url, init);

  // 204 success (no body)
  if (res.status === 204) {
    return undefined as unknown as T;
  }

  const contentType = res.headers.get("content-type") ?? "";

  // CSV success case
  if (contentType.startsWith("text/csv")) {
    const csv = await res.text();
    return csv as unknown as T;
  }

  // Parse JSON envelope or raw text fallback
  let parsed: any = null;
  if (contentType.includes("application/json")) {
    parsed = await res.json().catch(() => null);
  } else {
    const text = await res.text().catch(() => null);
    parsed = text;
  }

  if (
    contentType.includes("application/json") &&
    parsed &&
    typeof parsed === "object" &&
    "success" in parsed
  ) {
    const envelope = parsed as ApiEnvelope<T>;
    if (envelope.success) {
      return (envelope.data ?? undefined) as T;
    }

    if (expectsAuth && res.status === 401) {
      throw new ApiError({
        message: envelope.message || "Auth expired",
        httpStatus: res.status,
        errorCode: envelope.error?.code,
        errorDetails: envelope.error?.details,
        rawBody: envelope,
      });
    }

    throw new ApiError({
      message: envelope.message || "Request failed",
      httpStatus: res.status,
      errorCode: envelope.error?.code,
      errorDetails: envelope.error?.details,
      rawBody: envelope,
    });
  }

  if (!res.ok) {
    throw new ApiError({
      message: `Request failed: ${res.status} ${res.statusText}`.trim(),
      httpStatus: res.status,
      rawBody: parsed,
    });
  }

  return parsed as T;
}

async function doFetchEnvelope<T>(
  url: string,
  init: RequestInit,
  expectsAuth: boolean,
): Promise<ApiEnvelope<T>> {
  const res = await fetch(url, init);

  if (res.status === 204) {
    return { success: true, data: undefined } as ApiEnvelope<T>;
  }

  const contentType = res.headers.get("content-type") ?? "";
  let parsed: any = null;

  if (contentType.includes("application/json")) {
    parsed = await res.json().catch(() => null);
  } else {
    const text = await res.text().catch(() => null);
    parsed = text;
  }

  if (
    contentType.includes("application/json") &&
    parsed &&
    typeof parsed === "object" &&
    "success" in parsed
  ) {
    const envelope = parsed as ApiEnvelope<T>;
    if (envelope.success) {
      return envelope;
    }

    if (expectsAuth && res.status === 401) {
      throw new ApiError({
        message: envelope.message || "Auth expired",
        httpStatus: res.status,
        errorCode: envelope.error?.code,
        errorDetails: envelope.error?.details,
        rawBody: envelope,
      });
    }

    throw new ApiError({
      message: envelope.message || "Request failed",
      httpStatus: res.status,
      errorCode: envelope.error?.code,
      errorDetails: envelope.error?.details,
      rawBody: envelope,
    });
  }

  if (!res.ok) {
    throw new ApiError({
      message: `Request failed: ${res.status} ${res.statusText}`.trim(),
      httpStatus: res.status,
      rawBody: parsed,
    });
  }

  return { success: true, data: parsed } as ApiEnvelope<T>;
}

async function refreshTokens(): Promise<AuthTokens | null> {
  const current = parseStoredTokens();
  if (!current) return null;

  const baseUrl = getApiBaseUrl();
  if (!baseUrl) return null;

  const res = await fetch(`${baseUrl}/auth/refresh`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ refreshToken: current.refresh }),
  });

  if (res.status === 204) {
    return null;
  }

  const contentType = res.headers.get("content-type") ?? "";
  if (!res.ok) {
    return null;
  }

  const payload = contentType.includes("application/json")
    ? await res.json().catch(() => null)
    : null;

  const tokens = payload?.data ?? payload;
  const access = tokens?.access ?? tokens?.accessToken;
  const refresh = tokens?.refresh ?? tokens?.refreshToken;
  if (access && refresh) {
    const normalized: AuthTokens = {
      access: String(access),
      refresh: String(refresh),
    };
    persistTokens(normalized);
    return normalized;
  }

  return null;
}

/**
 * Mock-friendly API request helper with envelope parsing and reactive refresh (401/1000 only).
 */
export async function apiRequest<TResponse = unknown, TBody = unknown>(
  options: ApiRequestOptions<TBody, TResponse>,
): Promise<TResponse> {
  const {
    path,
    method = "GET",
    body,
    headers,
    signal,
    useMock,
    mockResponse,
    delayMs,
    auth = true,
    allowRefresh = true,
  } = options;

  const baseUrl = getApiBaseUrl();

  // Mock short-circuit
  if (useMock || !baseUrl) {
    return mockFetch(mockResponse as TResponse, delayMs);
  }

  const attempt = async (retried: boolean): Promise<TResponse> => {
    const tokens = auth ? parseStoredTokens() : null;
    const url = `${baseUrl}${path}`;
    const init: RequestInit = {
      method,
      signal,
      headers: buildHeaders(
        headers,
        body,
        auth ? (tokens?.access ?? null) : null,
        auth,
      ),
      body:
        body === undefined || body === null
          ? undefined
          : typeof body === "object" && !(body instanceof FormData)
            ? JSON.stringify(body)
            : (body as BodyInit),
    };

    try {
      return await doFetch<TResponse>(url, init, auth);
    } catch (err) {
      const apiErr = err as ApiError;
      const needsRefresh = auth && allowRefresh && apiErr.httpStatus === 401;

      if (needsRefresh) {
        if (retried) {
          hardLogout();
          throw apiErr;
        }

        if (!refreshInFlight) {
          refreshInFlight = refreshTokens();
        }

        await refreshInFlight.catch(() => null);
        refreshInFlight = null;

        const updatedTokens = parseStoredTokens();
        if (!updatedTokens) {
          hardLogout();
          throw apiErr;
        }

        return attempt(true);
      }

      throw apiErr;
    }
  };

  return attempt(false);
}

export async function apiRequestEnvelope<TResponse = unknown, TBody = unknown>(
  options: ApiRequestOptions<TBody, TResponse>,
): Promise<ApiEnvelope<TResponse>> {
  const {
    path,
    method = "GET",
    body,
    headers,
    signal,
    useMock,
    mockResponse,
    delayMs,
    auth = true,
    allowRefresh = true,
  } = options;

  const baseUrl = getApiBaseUrl();

  if (useMock || !baseUrl) {
    return {
      success: true,
      data: (await mockFetch(mockResponse as TResponse, delayMs)) as TResponse,
    } as ApiEnvelope<TResponse>;
  }

  const attempt = async (retried: boolean): Promise<ApiEnvelope<TResponse>> => {
    const tokens = auth ? parseStoredTokens() : null;
    const url = `${baseUrl}${path}`;
    const init: RequestInit = {
      method,
      signal,
      headers: buildHeaders(
        headers,
        body,
        auth ? (tokens?.access ?? null) : null,
        auth,
      ),
      body:
        body === undefined || body === null
          ? undefined
          : typeof body === "object" && !(body instanceof FormData)
            ? JSON.stringify(body)
            : (body as BodyInit),
    };

    try {
      return await doFetchEnvelope<TResponse>(url, init, auth);
    } catch (err) {
      const apiErr = err as ApiError;
      const needsRefresh = auth && allowRefresh && apiErr.httpStatus === 401;

      if (needsRefresh) {
        if (retried) {
          hardLogout();
          throw apiErr;
        }

        if (!refreshInFlight) {
          refreshInFlight = refreshTokens();
        }

        await refreshInFlight.catch(() => null);
        refreshInFlight = null;

        const updatedTokens = parseStoredTokens();
        if (!updatedTokens) {
          hardLogout();
          throw apiErr;
        }

        return attempt(true);
      }

      throw apiErr;
    }
  };

  return attempt(false);
}

/**
 * Resolve with mock data after an optional delay (default 400ms).
 */
export function mockFetch<T>(data: T, delayMs: number = 400): Promise<T> {
  return new Promise((resolve) => {
    window.setTimeout(() => resolve(data), Math.max(0, delayMs));
  });
}

/**
 * Reject with ApiError after an optional delay (useful for simulating failures).
 */
export function mockReject(
  status: number,
  message: string,
  delayMs: number = 400,
): Promise<never> {
  return new Promise((_, reject) => {
    window.setTimeout(
      () => reject(new ApiError({ message, httpStatus: status })),
      Math.max(0, delayMs),
    );
  });
}
